import {PolymerElement, html} from '@polymer/polymer';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status.js';
import {FlattenedNodesObserver} from '@polymer/polymer/lib/utils/flattened-nodes-observer.js';
import '@polymer/polymer/lib/elements/dom-if.js';
import '@polymer/polymer/lib/elements/dom-repeat.js';
import {ScopeBinding, ScopeRoot} from '../behaviors/keli-binding-behavior.js';
import {ResizableBehavior} from '../behaviors/resizable-behavior.js';
import {HotkeyBehavior} from '../behaviors/hotkey-behavior.js';
import {differenceInSeconds} from 'date-fns';
import {parseDateAndTime} from '@beauwest/business-date-parser';

class KeliDiscussion extends ScopeRoot(ScopeBinding(HotkeyBehavior(ResizableBehavior(PolymerElement)))) {
  static get template() {
    return html`
<style>
  :host {
    display: flex;
    flex-direction: column;
    overflow: visible;
    position: relative;
    min-height: 80px;
    max-height: 300px;
    background-color: var(--keli-background-color);
    border: 1px solid var(--keli-field-border-color);
  }

  #messages {
    overflow: auto;
    flex: 1;
    background-color: var(--keli-subtle-background);
  }

  #new-message {
    display: flex;
    flex-direction: row;
    height: auto;
    max-height: 120px;
    border-top: 1px solid var(--keli-field-border-color);
  }

  :host(.focused) #new-message {
    outline: 2px solid var(--keli-field-border-color-active);
  }

  :host(.reverse) #new-message {
    border-top: none;
    order: 1;
    border-bottom: 1px solid var(--keli-field-border-color);
  }

  :host(.reverse) #messages {
    order: 2;
  }

  :host(.readOnly) #new-message {
    display: none;
  }

  :host(.readOnly) .actions {
    display: none !important;
  }

  #input {
    flex: 1;
    padding: 0;
    --keli-input-field-padding: .5em 40px .5em .5em;
  }

  #submit {
    margin: 0 !important;
    flex: none !important;
    width: auto !important;
  }

  #emojiMenuIcon {
    display: inline-block;
    font-size: 24px;
    position: absolute;
    right: 85px;
    bottom: 7px;
    color: var(--keli-field-icon-color);
    cursor: pointer;
  }

  #emojiMenuIcon:hover {
    color: var(--keli-field-icon-hover-color);
  }

  #emojiMenu {
    position: absolute;
    right: 5px;
    bottom: 35px;
    width: 255px;
    height: 250px;
    z-index: 1;
    background: var(--keli-background-color);
    box-shadow: var(--keli-shadow-z1);
    border: var(--keli-container-border);
    overflow: auto;
  }

  #emojiMenuSearch {
    display: block;
    width: 100%;
    box-sizing: border-box;
    padding: .3em;
    font-size: 1em;
    border: none;
    border-bottom: 1px solid var(--keli-field-border-color);
    margin-bottom: .5em;
    background-color: var(--keli-field-background);
    color: var(--keli-text);
    outline: none;
  }

  #emojiMenu .emoji {
    height: auto;
    width: 24px;
    padding: 5px;
    cursor: pointer;
  }

  #emojiMenu .hidden {
    display: none;
  }

  #emojiMenu .emoji:hover {
    background-color: var(--keli-action-hover-background);
  }

  #usersTyping {
    position: absolute;
    bottom: -1.1em;
    font-size: .7em;
    left: 5px;
    color: var(--keli-text-subtle);
  }

  #resizeBottom {
    display: var(--keli-discussion-resize-handle-display, block);
    position: absolute;
    min-height: 8px;
    opacity: 0;
    cursor: ns-resize;
    bottom: -3px;
    right: 0;
    left: 0;
  }
</style>

<keli-discussion-controller id="controller" keliname="_t"></keli-discussion-controller>
<div id="messages"></div>
<div id="new-message">
  <keli-textarea id="input" placeholder="Send a message&hellip;" height="40" class="styleless" propagate-enter prevent-panel-events prevent-adjacent-element></keli-textarea>
  <keli-button id="submit" caption="Send" submit="[[submitObject]]" keliname="submit_discussion_message" background="700" prevent-adjacent-element force-propagation></keli-button>

  <sl-icon name="sticker-emoji" id="emojiMenuIcon" on-click="handleEmojiMenuToggle"></sl-icon>
  <template is="dom-if" if="[[emojiMenuActive]]">
    <div id="emojiMenu">
      <input type="text" id="emojiMenuSearch" placeholder="Search emoji" on-input="handleEmojiMenuSearch">
      <template is="dom-repeat" items="[[emojiMenuItems]]">
        <span on-click="handleEmojiClick" data-name="[[item]]">[[item]]</span>
      </template>
    </div>
  </template>
</div>
<template is="dom-if" if="[[usersTypingString]]">
  <div id="usersTyping">[[usersTypingString]]</div>
</template>
<div id="resizeBottom"></div>`;
  }

  static get is() {
    return 'keli-discussion';
  }

  static get properties() {
    return {
      disabled: Boolean,
      emojiMenuActive: {type: Boolean, observer: 'emojiMenuActiveChanged'},
      emojiMenuItems: Array,
      height: {type: Number, scopevar: {options: {view: true}}, observer: 'heightChanged'},
      isBlockPanelElement: {type: Boolean, value: true},
      keliControlTypes: {type: Array, value: ['discussion']},
      needsDataLoaded: {type: Boolean, value: true},
      readOnly: {type: Boolean, observer: 'readOnlyChanged'},
      reverse: {type: Boolean, observer: 'reverseChanged'},
      submitObject: {
        type: Object, value: () => {
          return {message: ''};
        }
      },
      textCasing: {type: String, observer: 'textCasingChanged'},
      usersTyping: {type: Array, scopevar: true},
      usersTypingString: {type: String, computed: 'computeUsersTypingString(usersTyping, usersTypingCounter)'},
      usersTypingCounter: {type: Number, value: 0}
    }
  }

  constructor() {
    super();
    this.addEmitToKeli(['keliFocus', 'keliBlur']);
  }

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('focusable-control');

    this.addEventListener('focusElement', this.handleFocusElement);
    this.addEventListener('keliUserChange', this.handleKeliUserChange);
    this.addEventListener('keliActionActivated', this.handleKeliActionActivated);

    this._boundHandleInputFocus = this.handleInputFocus.bind(this);
    this._boundHandleInputBlur = this.handleInputBlur.bind(this);
    this.$.input.addEventListener('keliFocus', this._boundHandleInputFocus);
    this.$.input.addEventListener('keliBlur', this._boundHandleInputBlur);

    this.resizableParentElement = this;
    this.resizableHandleElements = [
      {element: this.$.resizeBottom, direction: 's'}
    ];

    this._observer = new FlattenedNodesObserver(this.$.messages, (info) => {
      if (info.addedNodes.length > 0) {
        this.scrollToLatest();
      }
    });

    this.$.submit.focusOnActivation = false;
    this.dispatchEvent(new CustomEvent('panelElementCreated', {
      bubbles: true,
      composed: true,
      detail: {element: this}
    }));

    this.registerHotkey(KeliInterface.Hotkeys.discussion.toggleEmoji, {
      down: () => {
        this.handleEmojiMenuToggle();
      }
    });

    this.addHotkeyListener('raw-listener', this, {forceDefaults: true});
    this.registerHotkey(KeliInterface.Hotkeys.discussion.send, {
      down: event => {
        event.stopPropagation();
        if (this.emojiMenuActive) {
          event.preventDefault();
          this.handleEmojiClick(event, this.firstVisibleEmoji);
          return;
        }
        if (!event.shiftKey) {
          event.preventDefault();
          this.$.submit.activate(event);
        }
      }
    }, 'raw-listener');
    this.registerHotkey(KeliInterface.Hotkeys.discussion.closeEmoji, {
      down: event => {
        if (this.emojiMenuActive) {
          event.stopPropagation();
          event.preventDefault();
          this.handleEmojiMenuToggle();
        }
      }
    }, 'raw-listener');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('focusElement', this.handleFocusElement);
    this.removeEventListener('keliUserChange', this.handleKeliUserChange);
    this.removeEventListener('keliActionActivated', this.handleKeliActionActivated);
    this.$.input.removeEventListener('keliFocus', this._boundHandleInputFocus);
    this.$.input.removeEventListener('keliBlur', this._boundHandleInputBlur);

    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  async keliReady(scope) {
    super.keliReady && await super.keliReady(scope);
    await this.$.controller.elementReady(this);
  }

  emojiMenuActiveChanged(newValue) {
    if (newValue) {
      afterNextRender(this, () => {
        this.shadowRoot.querySelector('#emojiMenu').scrollTop = 0;
        this.shadowRoot.querySelector('#emojiMenuSearch').focus();
      });
    } else if (this.emojiMenuItems) {
      this.shadowRoot.querySelector('#emojiMenuSearch').value = '';
      this.handleEmojiMenuSearch();
      afterNextRender(this, () => {
        this.$.input.$.input.focus();
      });
    }
  }

  heightChanged(newValue) {
    if (newValue) {
      this.style.height = this.style.maxHeight = `${parseInt(newValue, 10)}px`;
    } else {
      this.style.height = this.style.maxHeight = '';
    }
  }

  readOnlyChanged(newValue) {
    this.classList.toggle('readOnly', newValue);
    this.disabled = newValue;
  }

  reverseChanged(newValue) {
    this.classList.toggle('reverse', newValue);
  }

  textCasingChanged(newValue) {
    const property = newValue?.toLowerCase();
    if (property && ['uppercase', 'lowercase', 'capitalize'].includes(property)) {
      this.$.input.$.input.style.textTransform = property;
    } else {
      this.$.input.$.input.textTransform = '';
    }
  }

  computeUsersTypingString(usersTyping, usersTypingCounter) {
    const names = [];
    if (usersTyping && usersTyping.length) {
      usersTyping.forEach(({last, name}) => {
        if (name) {
          const secondsAgo = Math.abs(differenceInSeconds(new Date(), parseDateAndTime(last)));
          if (secondsAgo < 15) {
            names.push(name);
          }
        }
      });
    }
    let typingString;
    if (names && names.length) {
      switch (names.length) {
        case 1:
          typingString = `${names[0]} is typing`;
          break;
        case 2:
          typingString = `${names.join(' and ')} are typing`;
          break;
        default:
          typingString = 'Several people are typing';
          break;
      }
    }

    if (typingString) {
      this._usersTypingTimeout = window.setTimeout(() => {
        ++this.usersTypingCounter;
      }, 1000);
    } else {
      window.clearTimeout(this._usersTypingTimeout);
    }

    return typingString;
  }

  handleEmojiMenuToggle() {
    this.emojiMenuActive = !this.emojiMenuActive;

    // Build the emoji list if we haven't already
    if (!this.emojiMenuItems) {
      this.emojiMenuItems = emojify.emojiNames.map(name => `:${name}:`);
      afterNextRender(this, () => {
        const element = this.shadowRoot.querySelector('#emojiMenu');
        for (let i = 0; i < element.childElementCount; ++i) {
          if (element.children[i].tagName === 'SPAN') {
            emojify.run(element.children[i]);
          }
        }

        // Set up the matcher.
        const elementList = [];
        const elements = element.querySelectorAll('span');
        for (let i = 0; i < elements.length; ++i) {
          elementList.push({
            name: elements[i].dataName,
            element: elements[i]
          });
        }
        this.emojiMenuMatcher = new Matchizzle(elementList, {
          keys: ['name'],
          id: 'element'
        });
      });
    }
  }

  handleEmojiMenuSearch() {
    this.firstVisibleEmoji = undefined;
    const searchValue = this.shadowRoot.querySelector('#emojiMenuSearch').value;
    const elements = this.shadowRoot.querySelector('#emojiMenu').querySelectorAll('span');

    if (!searchValue) {
      elements.forEach((item) => {
        item.classList.remove('hidden');
      });
    } else {
      const visibleElements = this.emojiMenuMatcher.search(searchValue);
      elements.forEach((item) => {
        if (visibleElements.includes(item)) {
          item.classList.remove('hidden');
        } else {
          item.classList.add('hidden');
        }
      });

      // Set the first emoji so we can pick it
      if (visibleElements && visibleElements.length) {
        this.firstVisibleEmoji = visibleElements[0];
      }
    }
  }

  handleEmojiClick(event, target) {
    if (event) {
      target = getEventTarget(event);
    }
    if (!target) {
      return;
    }
    const emojiString = target.getAttribute('title') || target.dataName;
    if (!emojiString) {
      return;
    }

    // Insert the text into the field.
    const inputField = this.$.input.$.input;
    if (inputField) {
      const insertPosition = inputField.selectionStart + emojiString.length;
      if (inputField.selectionStart || inputField.selectionStart === 0) {
        const startPos = inputField.selectionStart;
        const endPos = inputField.selectionEnd;
        inputField.value = inputField.value.substring(0, startPos) + emojiString + inputField.value.substring(endPos, inputField.value.length);
      } else {
        inputField.value += emojiString;
      }
      this.$.input._handleInput();
      inputField.focus();
      inputField.setSelectionRange(insertPosition, insertPosition);
    }

    this.emojiMenuActive = false;
  }

  handleFocusElement() {
    if (!this.disabled) {
      this.$.input.dispatchEvent(new CustomEvent('focusElement'));
    }
  }

  handleInputFocus(event) {
    this.classList.add('focused');
    event.stopImmediatePropagation();
    this.dispatchEvent(new CustomEvent('keliFocus', {
      bubbles: true,
      composed: true,
      detail: {element: this, event: event && event.detail.event}
    }));
    this.dispatchEvent(new CustomEvent('setPanelActiveElement', {
      bubbles: true,
      composed: true,
      detail: {element: this}
    }));
    this.emojiMenuActive = false;
  }

  handleInputBlur(event) {
    this.classList.remove('focused');
    event.stopImmediatePropagation();
    this.dispatchEvent(new CustomEvent('keliBlur', {
      bubbles: true,
      composed: true,
      detail: {element: this, event: event && event.detail.event}
    }));
    this.updateInputHeight();
  }

  handleKeliUserChange(event) {
    if (event && event.detail.element === this.$.input) {
      this.unsavedChanges = true;
      this.$.controller.unsavedText = event.detail.newValue;
      this.submitObject.message = event.detail.newValue;
      this.updateInputHeight(false);
    }
  }

  handleKeliActionActivated() {
    if (!this.submitObject.message) {
      return;
    }

    this.$.controller.unsavedTextChanged.stop();
    this.$.controller.unsavedText = undefined;
    this.unsavedChanges = false;

    // Create a pending message based on what the user typed in.
    this.$.messages.appendChild(this.createNewMessage({
      message: this.submitObject.message,
      unsaved: true
    }));

    this.submitObject.message = '';
    this.$.input.resetToDefaultValue('');
    this.$.input.dispatchEvent(new CustomEvent('focusElement'));
    this.updateInputHeight();
  }

  handleResizableResizeElement(event) {
    event.stopPropagation();
    if (event.detail.height) {
      this.height = event.detail.height;
    }
  }

  handleResizableResizeElementEnded(event) {
    event.stopPropagation();
  }

  updateInputHeight(reset = true) {
    const defaultHeight = 40;
    const maxHeight = 120;
    let newTextHeight = defaultHeight;

    if (reset) {
      this.$.input.height = defaultHeight;
    }
    afterNextRender(this.$.input.$.input, () => {
      if (this.$.input.currentValue) {
        newTextHeight = this.$.input.$.input.scrollHeight;
      }
      this.$.input.height = Math.min(Math.max(newTextHeight, defaultHeight), maxHeight);
    });
  }

  scrollToLatest() {
    afterNextRender(this.$.messages, () => {
      if (this.reverse) {
        this.$.messages.scrollTop = 0;
      } else {
        this.$.messages.scrollTop = this.$.messages.scrollHeight;
      }
    });
  }

  createNewMessage(options) {
    const base = document.createElement('keli-discussion-message');
    base.systemMessage = options.systemMessage || undefined;
    base.id = options.id || undefined;
    base.message = options.message || undefined;
    base.person = options.person || undefined;
    base.time = options.time || undefined;
    base.unsaved = options.unsaved || undefined;
    base.editAddress = options.editAddress || undefined;
    return base;
  }

  _keliAccept(event, isClosingPanel) {
    if (this.$.controller) {
      if (this.$.controller?.unsavedTextChanged?.pending) {
        this.$.controller.unsavedTextChanged.execute();
      }
      if (isClosingPanel && this.$.input?.currentValue && !this.$.controller.readOnly && this.$.controller.sendOnClose) {
        this.$.submit?.activate(event);
      }
    }
  }
}

customElements.define(KeliDiscussion.is, KeliDiscussion);
export {KeliDiscussion};
